import ContactProfileImage from '@app/components/ContactProfileImage'
import PageSection from '@app/components/PageSection'
import { ICurrentUserState } from '@app/store/modules/account'
import {
  IGlobalRolesState,
  IGlobalTeamsState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatPhoneNumber, humanize } from '@app/utils/formatting'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, RouteComponentProps } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, IGuestViewState } from '../modules/guestView'

interface IComponentRouterProps {
  id: string
}

type IComponentProps = IGuestViewState &
  ICurrentUserState &
  IGlobalRolesState &
  IGlobalTeamsState &
  RouteComponentProps<IComponentRouterProps> &
  typeof actionCreators

export default class GuestViewPage extends React.PureComponent<
  IComponentProps
> {
  private userId: string

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)
  }

  public componentDidMount() {
    const { id } = this.props.match.params
    this.userId = id
    this.props.loadGuest(id)
  }

  public componentDidUpdate(
    prevProps: IComponentProps,
    prevState: any,
    snapshot: any,
  ) {
    const { id } = this.props.match.params
    if (prevProps.item && prevProps.item.id !== id) {
      this.userId = id
      this.props.loadGuest(id)
    }
  }

  public render() {
    if (!this.props.item || !this.props.user) {
      return <div />
    }

    return (
      <DocumentTitle title={this.props.item.fullName}>
        <Container className='mt-3'>
          <Card className='d-flex flex-column mt-2'>
            <CardHeader className='bg-secondary text-white'>
              {this._renderCardHeader()}
            </CardHeader>
            <CardBody>{this._renderCardBody()}</CardBody>
          </Card>
          <PageSection title='Access'>{this._renderAccess()}</PageSection>
        </Container>
      </DocumentTitle>
    )
  }

  private _renderAccess() {
    if (!this.props.item.user) {
      return null
    }

    if (
      !this.props.item.user.allowedAccessTo ||
      this.props.item.user.allowedAccessTo.size === 0
    ) {
      return 'No specific access rules applied'
    }

    const access = this.props.item.user.allowedAccessTo.map((item: any) => {
      return (
        <div
          key={item}
          style={{ borderBottom: '1px solid #ddd' }}
          className='d-flex flex-row flex-wrap justify-content-between mt-2'
        >
          <div>
            <strong>{item.name}</strong>
          </div>
          <div>{humanize(item.value)}</div>
        </div>
      )
    })

    return <div>{access}</div>
  }

  private _renderCardBody() {
    return (
      <div className='d-flex flex-row flex-wrap'>
        <ContactProfileImage contact={this.props.item.id} size={70} />

        <div className='d-flex flex-column ml-3 align-self-center'>
          <div style={{ fontWeight: 'bold' }}>{this.props.item.fullName}</div>
          <div>
            {this.props.item.company ? this.props.item.company.name : ''}
          </div>
          <div>{this.props.item.title}</div>
        </div>

        <div className='d-flex flex-row flex-wrap mt-2 mt-md-0 ml-md-auto'>
          {this._renderPhoneNumbers()}
          {this._renderEmailAddresses()}
        </div>
      </div>
    )
  }

  private _renderEmailAddresses() {
    return (
      <div>
        {this.props.item.emailAddresses.map((email: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`email-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(email.type)}</div>
              <div className='ml-auto'>
                <a href={`mailto:${email.emailAddress}`}>
                  {email.emailAddress}
                </a>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _renderPhoneNumbers() {
    return (
      <div className='mr-2'>
        {this.props.item.phoneNumbers.map((phone: any, i) => {
          return (
            <div
              className='d-flex flex-row'
              style={{ fontSize: '0.875rem' }}
              key={`phone-${i}`}
            >
              <div className='text-muted mr-2'>{humanize(phone.type)}</div>
              <div className='phone-number ml-auto'>
                {formatPhoneNumber(phone.phoneNumber)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private _renderCardHeader() {
    if (!this.props.user) {
      return null
    }

    const connectionHeaderIcon: [IconPrefix, IconName] = ['fal', 'user']

    return (
      <div className='d-flex flex-row'>
        <span>
          <FontAwesomeIcon
            className='primary-color'
            icon={connectionHeaderIcon}
            fixedWidth
          />
          {this.props.item.user
            ? `User (${this.props.item.user.status})`
            : 'User'}
        </span>
        <div className='ml-auto'>
          {this._canEdit() && (
            <UncontrolledDropdown style={{ display: 'inline' }}>
              <DropdownToggle tag='a' className='px-1 text-center'>
                <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
              </DropdownToggle>
              {this._renderDropdown()}
            </UncontrolledDropdown>
          )}
        </div>
      </div>
    )
  }

  private _renderDropdown() {
    const menuItems = []

    if (this._canEdit()) {
      menuItems.push(
        <Link key='edit' to={`/admin/guest/${this.userId}/edit`}>
          <DropdownItem>Edit</DropdownItem>
        </Link>,
      )
    }

    return <DropdownMenu>{menuItems}</DropdownMenu>
  }

  private _canEdit() {
    if (!this.props.user) {
      return false
    }

    if (
      this.props.item.user &&
      this.props.item.user.status === 'System' &&
      this.props.item.user.permissions.isAdmin === true
    ) {
      return false
    }

    return true
  }
}
